import { Dispatch, SetStateAction } from "react";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { ViewSchema, ViewType } from "@common/types/views/views";
import { createActionSectionTrigger } from "@components/drawers/actionSectionTrigger";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerChevron,
    PickerTriggerElement,
} from "@design-system/Picker/Picker.types";
import {
    TASK_TYPE_OPTION_GROUPS,
    isAccrualReviewPreset,
    isTaskTypeOption,
    type TaskTypeOption,
} from "./closeTemplateTasksDrawer.constants";
import { createDefaultViewConfigForTaskTypeOption } from "./taskTypeOptionViewConfig";
import {
    isOrgTaskTemplatePickerValue,
    parseOrgTaskTemplateIdFromPickerValue,
} from "./orgTaskTemplatePicker.utils";
import {
    buildWorkflowPickerItems,
    type WorkflowPickerValue,
} from "./workflowPickerItems";

interface TaskTypePickerProps {
    taskType: TaskTypeOption;
    viewConfig: Record<string, unknown> | null;
    onTaskTypeChange: (option: TaskTypeOption) => void;
    onViewConfigChange: Dispatch<
        SetStateAction<Record<string, unknown> | null>
    >;
    basis: CloseTemplateBasis | null;
    orgTaskTemplates?: readonly OrganizationTaskTemplate[];
    onOrgTaskTemplateSelect?: (template: OrganizationTaskTemplate) => void;
    trigger?: PickerTriggerElement;
    chevron?: PickerChevron;
    triggerClassName?: string;
}

export const TaskTypePicker = ({
    taskType,
    viewConfig,
    onTaskTypeChange,
    onViewConfigChange,
    basis,
    orgTaskTemplates = [],
    onOrgTaskTemplateSelect,
    trigger,
    chevron,
    triggerClassName,
}: TaskTypePickerProps) => {
    const isCashBasis = basis === CloseTemplateBasis.MODIFIED_CASH_LEDGER;
    const isAccrualReviewSelected = isAccrualReviewPreset(taskType);

    const visibleGroups = TASK_TYPE_OPTION_GROUPS.map((group) => ({
        ...group,
        options:
            isCashBasis && !isAccrualReviewSelected
                ? group.options.filter(
                      (opt) => !isAccrualReviewPreset(opt.value),
                  )
                : group.options,
    }));

    const pickerItems = buildWorkflowPickerItems({
        groups: visibleGroups,
        orgTaskTemplates,
    });

    const handleValueChange = (value: WorkflowPickerValue | null) => {
        if (value !== null && isOrgTaskTemplatePickerValue(value)) {
            const templateId = parseOrgTaskTemplateIdFromPickerValue(value);
            const template = orgTaskTemplates.find(
                (candidate) => candidate.id === templateId,
            );
            if (template) {
                onOrgTaskTemplateSelect?.(template);
            }
            return;
        }
        if (value === null || !isTaskTypeOption(value)) {
            return;
        }
        onTaskTypeChange(value);
        const defaultViewConfig = createDefaultViewConfigForTaskTypeOption({
            option: value,
            basis,
        });
        if (defaultViewConfig) {
            onViewConfigChange(defaultViewConfig);
        } else {
            const parsed = ViewSchema.safeParse(viewConfig);
            if (
                parsed.success &&
                (parsed.data.type === ViewType.TRANSACTION ||
                    parsed.data.type === ViewType.PROFIT_LOSS ||
                    parsed.data.type === ViewType.BALANCE_SHEET ||
                    parsed.data.type === ViewType.COUNTERPARTY_DIRECTORY ||
                    parsed.data.type === ViewType.INVOICE ||
                    parsed.data.type === ViewType.FINANCIAL_DOCUMENT ||
                    parsed.data.type === ViewType.RECONCILIATION)
            ) {
                onViewConfigChange(null);
            }
        }
    };

    return (
        <Picker.Select
            items={pickerItems}
            nested="flyout"
            searchable
            searchPlaceholder="Search actions…"
            value={taskType}
            onValueChange={handleValueChange}
            chevron={chevron}
            trigger={trigger ?? createActionSectionTrigger(triggerClassName)}
        />
    );
};
